'use client';

import { useCallback, useEffect, useState } from 'react';

import Image from 'next/image';
import Link from '~/(tenant)/_components/tenant-link';
import { usePathname } from 'next/navigation';

import { useQuery } from '@tanstack/react-query';
import { Calendar, Menu, Package, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { cn } from '@kit/ui/utils';

import { persistLanguage } from '~/lib/i18n/persist-language';

import { KaramaHeaderAccountSection } from './header-account-section';

// Founder name is a tenant-specific brand label; localized inline rather than
// in the global landing namespace so it doesn't pollute the shared keyspace.
const FOUNDER_NAME = {
  en: 'LANA NAZER',
  ar: 'لنا ناظر',
};

/**
 * EN / AR pill toggle. Click switches to the other supported locale, persists
 * it in the lang cookie, and reloads so the SSR shell (and `dir`) re-renders.
 * `onDark` styles it for the transparent navbar over the hero; otherwise it
 * uses the brand purple border with a pink hover accent (tri-color theme).
 */
function KaramaLanguageToggle({
  onDark = false,
  className,
}: {
  onDark?: boolean;
  className?: string;
}) {
  const { i18n } = useTranslation();

  const supported = (
    i18n.options.supportedLngs as string[] | undefined
  )?.filter((l) => l.toLowerCase() !== 'cimode') ?? ['en'];

  const current = i18n.language?.split('-')[0] ?? 'en';
  const next = supported.find((l) => l !== current) ?? current;

  const handleClick = useCallback(async () => {
    await persistLanguage(i18n, next);
    window.location.reload();
  }, [i18n, next]);

  const activeColor = onDark ? 'text-white' : 'text-[var(--karama-primary)]';
  const inactiveColor = onDark ? 'text-white/45' : 'text-gray-400';

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={`Switch language to ${next.toUpperCase()}`}
      className={cn(
        'inline-flex h-10 items-center gap-1.5 rounded-full border-2 px-4 text-xs font-bold tracking-wider whitespace-nowrap transition-colors duration-200',
        onDark
          ? 'border-white/60 hover:border-[var(--karama-pink)]'
          : 'border-[var(--karama-primary)]/70 hover:border-[var(--karama-pink)]',
        className,
      )}
    >
      <span className={current === 'en' ? activeColor : inactiveColor}>EN</span>
      <span className={onDark ? 'text-white/30' : 'text-gray-300'} aria-hidden>
        /
      </span>
      <span className={current === 'ar' ? activeColor : inactiveColor}>AR</span>
    </button>
  );
}

// Primary landing nav. Order MIRRORS the section order on the landing page
// (index.tsx default export): Schedule, Pricing, About, This Month,
// Testimonials, Teacher Training, Corporate. "Classes" jumps to the live
// timetable (#schedule) — a separate "Schedule" entry pointed at the same
// anchor and was dropped as redundant.
// "Community" points at the testimonials section (#reviews). Location was
// intentionally dropped from the nav (the section still exists).
const navLinks = [
  { labelKey: 'landing:nav.classes', fallback: 'CLASSES', href: '/#schedule' },
  {
    labelKey: 'landing:nav.pricing',
    fallback: 'PRICING',
    href: '/#pricing',
  },
  { labelKey: 'landing:nav.about', fallback: 'ABOUT', href: '/#about' },
  {
    labelKey: 'landing:nav.thisMonth',
    fallback: 'THIS MONTH',
    href: '/#this-month',
  },
  {
    labelKey: 'landing:nav.community',
    fallback: 'COMMUNITY',
    href: '/#reviews',
  },
  {
    labelKey: 'landing:nav.teacherTraining',
    fallback: 'TEACHER TRAINING',
    href: '/#teacher-training',
  },
  {
    labelKey: 'landing:nav.corporate',
    fallback: 'CORPORATE & COLLABS',
    href: '/#corporate',
  },
];

// Authenticated visitors get a TRIMMED in-page nav so the desktop bar still
// fits once the account links (My Bookings / My Packages) are appended.
// The marketing-heavy tail (This Month, Teacher Training, Corporate, About)
// is dropped for logged-in users; it remains in the mobile drawer (which lists
// the full navLinks) and on the logged-out bar.
const authDesktopNavLinks = navLinks.filter((link) =>
  [
    'landing:nav.classes',
    'landing:nav.pricing',
    'landing:nav.community',
  ].includes(link.labelKey),
);

const authNavLinks = [
  {
    labelKey: 'landing:nav.myBookings',
    fallback: 'MY BOOKINGS',
    href: '/bookings',
    icon: Calendar,
  },
  {
    labelKey: 'landing:nav.myPackages',
    fallback: 'MY PACKAGES',
    href: '/packages',
    icon: Package,
  },
];


interface KaramaHeaderProps {
  logoUrl?: string | null;
}

export function KaramaHeader({ logoUrl }: KaramaHeaderProps) {
  const { t, i18n } = useTranslation();
  const i18nLang = i18n.language?.startsWith('ar') ? 'ar' : 'en';
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isAuthenticated = useIsAuthenticated();

  const isLandingPage = pathname === '/';

  // The navbar is ALWAYS solid white (even over the hero) so the full-colour
  // stacked logo and the dark nav text always sit on white and stay legible.
  // We previously floated a transparent bar over the hero, but the detailed
  // colour logo washed out against the photo. `scrolled` is still tracked for
  // the announcement strip below.
  const shouldBeSolid = true;

  // Logo/text never render in light mode now that the bar is always white.
  const onDark = false;

  // The announcement strip only belongs on the landing home, and tucks away
  // once the visitor starts scrolling so the navbar stays compact.
  const showAnnouncement = isLandingPage && !scrolled;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileMenuOpen) setMobileMenuOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileMenuOpen]);

  const desktopLinks = isAuthenticated ? authDesktopNavLinks : navLinks;

  return (
    <>
      <header className="fixed top-0 right-0 left-0 z-50">
        {/* Announcement strip, landing home only, hides on scroll */}
        <div
          className={cn(
            'overflow-hidden text-center text-white transition-all duration-300',
            showAnnouncement ? 'max-h-10 py-2' : 'max-h-0 py-0',
          )}
          style={{ background: 'var(--karama-purple)' }}
        >
          <p className="font-karama-label px-4 text-[0.6rem] sm:text-[0.65rem]">
            {i18nLang === 'ar'
              ? 'أفضل استوديو يوغا في المملكة العربية السعودية · حائز على جائزة بريستيج ٦ مرات · جدة · منذ ٢٠١٦'
              : 'Best Yoga Studio in Saudi Arabia · 6× Prestige Award Winner · Jeddah · Est. 2016'}
          </p>
        </div>

        <div
          className={cn(
            'transition-all duration-300',
            shouldBeSolid
              ? 'bg-white py-2 shadow-[0_1px_0_rgba(0,0,0,0.06)]'
              : 'bg-transparent py-4',
          )}
        >
          {/* Asymmetric padding: the logo hugs the left edge (pl-2/3/4) for a
              roomier bar, while the right side keeps full padding so the
              Book Now / Sign In pills don't touch the screen edge. */}
          <div className="mx-auto flex max-w-[1456px] items-center justify-between pr-4 pl-3 sm:pr-6 sm:pl-4 lg:pr-8 lg:pl-4">
            {/* Logo: the full stacked Karama mark (hamsa + KARAMA YOGA + Arabic).
                A tenant-uploaded custom logo always wins. The bar is always
                white, so the full-colour logo sits on white and stays legible;
                flex+items-center keeps it centred against the nav tabs. */}
            <Link
              href="/"
              className="relative z-10 flex flex-shrink-0 items-center"
            >
              <Image
                src={logoUrl || '/landing-pages/karama/images/logo-stacked.png'}
                alt="Karama Yoga Studio"
                width={778}
                height={900}
                // Taller than the old horizontal wordmark so the stacked
                // layout (icon over text) stays readable in the bar.
                className="h-16 w-auto object-contain sm:h-16 lg:h-[72px]"
                priority
              />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden items-center gap-x-4 pl-6 xl:flex 2xl:gap-x-5 2xl:pl-10">
              {desktopLinks.map((link) => (
                <Link
                  key={link.labelKey}
                  href={link.href}
                  className={cn(
                    'font-karama-label text-xs whitespace-nowrap transition-colors',
                    onDark
                      ? 'text-white/90 hover:text-white'
                      : 'text-[var(--karama-secondary)] hover:text-[var(--karama-primary)]',
                  )}
                >
                  {renderLabel(link, t, i18nLang)}
                </Link>
              ))}
              {isAuthenticated &&
                authNavLinks.map((link) => (
                  <Link
                    key={link.labelKey}
                    href={link.href}
                    className={cn(
                      'font-karama-label text-xs whitespace-nowrap transition-colors',
                      onDark
                        ? 'text-white/90 hover:text-white'
                        : 'text-[var(--karama-secondary)] hover:text-[var(--karama-primary)]',
                    )}
                  >
                    {renderLabel(link, t, i18nLang)}
                  </Link>
                ))}
            </nav>

            {/* Right side actions (desktop) */}
            <div className="hidden items-center gap-x-3 xl:flex">
              <KaramaLanguageToggle onDark={onDark} />
              <KaramaHeaderAccountSection onDark={onDark} />
            </div>

            {/* Right side actions (mobile/tablet), compact Book Now + Sign In
                live in the top bar next to the hamburger, mirroring the other
                tenant headers so the primary CTAs are always one tap away. */}
            <div className="flex shrink-0 items-center gap-2 xl:hidden">
              <KaramaLanguageToggle onDark={onDark} className="h-9 px-3" />
              <KaramaHeaderAccountSection onDark={onDark} compact />

              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={cn(
                  '-mr-2 p-2 transition-colors',
                  onDark ? 'text-white' : 'text-[var(--karama-secondary)]',
                )}
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu overlay. Anchored to the header's own bottom edge
            (absolute top-full) rather than a hardcoded fixed offset, Karama's
            header carries a purple announcement strip above the bar, so a
            fixed top-[64px] started ~40px too high and tinted the logo row
            grey. top-full always lines up with the real header bottom. */}
        {mobileMenuOpen && (
          <div
            className="absolute top-full right-0 left-0 z-40 h-screen bg-black/40 xl:hidden"
            onClick={() => setMobileMenuOpen(false)}
            onKeyDown={(e) => e.key === 'Escape' && setMobileMenuOpen(false)}
            role="button"
            tabIndex={0}
            aria-label="Close menu"
          />
        )}

        {/* Mobile menu drawer */}
        <div
          className={cn(
            'absolute top-full right-0 left-0 z-50 overflow-hidden bg-white shadow-lg transition-all duration-300 xl:hidden',
            mobileMenuOpen ? 'max-h-[600px]' : 'max-h-0',
          )}
        >
          <nav className="flex flex-col px-6 pb-6">
            {navLinks.map((link) => (
              <Link
                key={link.labelKey}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className="font-karama-label border-b border-black/5 py-3 text-sm text-[var(--karama-secondary)] transition-colors hover:text-[var(--karama-primary)]"
              >
                {renderLabel(link, t, i18nLang)}
              </Link>
            ))}
            {isAuthenticated &&
              authNavLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.labelKey}
                    href={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="font-karama-label flex items-center gap-3 border-b border-black/5 py-3 text-sm text-[var(--karama-secondary)] transition-colors hover:text-[var(--karama-primary)]"
                  >
                    <Icon className="h-4 w-4" />
                    {renderLabel(link, t, i18nLang)}
                  </Link>
                );
              })}
            <div className="pt-4">
              <KaramaHeaderAccountSection
                onNavigate={() => setMobileMenuOpen(false)}
              />
            </div>
          </nav>
        </div>
      </header>

      {/* Spacer: inner pages have no hero, so offset their content below the
          fixed navbar. Sized for the stacked logo: mobile h-16 (64px) + py-2
          (16px) = 80px; lg h-[72px] + py-2 = 88px.
          Landing handles its own offset via the 100vh hero. */}
      {!isLandingPage && <div className="h-[80px] lg:h-[88px]" aria-hidden />}
    </>
  );
}

function renderLabel(
  link: { labelKey: string; fallback: string },
  t: (key: string, opts: { defaultValue: string }) => string,
  lang: 'en' | 'ar',
) {
  if (link.labelKey === '__founder__') {
    return FOUNDER_NAME[lang];
  }
  return t(link.labelKey, { defaultValue: link.fallback });
}

function useIsAuthenticated() {
  const client = useSupabase();
  const { data } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data } = await client.auth.getSession();
      return data.session;
    },
  });
  return !!data;
}
